/**
 * <hero-carousel>
 * ---------------------------------------------------------------------------
 * Carrusel del banner principal.
 *
 * Se apoya en scroll-snap del navegador en lugar de animar posiciones a mano:
 * el gesto táctil sale gratis, el rendimiento es el nativo y sin JS las
 * diapositivas siguen siendo deslizables.
 *
 * La reproducción automática se para al pasar el ratón, al enfocar con el
 * teclado, al ocultarse la pestaña y si el visitante pidió menos movimiento.
 */

class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    if (this.slides.length < 2) return;

    this.index = 0;

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => this.go(Number(dot.dataset.heroDot)))
    );

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });

    if (this.dataset.autoplay === 'true' && !this.#prefersReducedMotion) {
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start() {
    this.stop();
    const speed = Number(this.dataset.autoplaySpeed) || 6000;
    this.timer = setInterval(() => this.go(this.index + 1), speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  go(target) {
    const next = (target + this.slides.length) % this.slides.length;
    this.track.scrollTo({
      left: this.slides[next].offsetLeft,
      behavior: this.#prefersReducedMotion ? 'auto' : 'smooth',
    });
    this.#setIndex(next);
  }

  #onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const current = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.#setIndex(current);
    });
  };

  #setIndex(index) {
    this.index = index;
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
